import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Image,
  Card,
  Modal,
  Spinner,
} from "react-bootstrap";

import { transformProfilePicture } from "../../components/transformFileURL";
import NeedCard from "../profile/widgets/_needCard";

import { getData, postData } from "../../services/apiService";

import noData from "../../resources/images/no-data.png";

/**
 * Component for displaying needs based on the selected organization.
 * @returns {React.Element} - Returns JSX for displaying needs by organization.
 */
const NeedsByOrganization = () => {
  // Extract the reference ID of the organization from the URL parameters
  const { referenceId } = useParams();
  const location = useLocation();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [organization, setOrganization] = useState({});
  const [needs, setNeeds] = useState([]);
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 25 });
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    /**
     * Fetches the organization profile from the backend API
     * Executes when there is a change in the values of the dependencies in the useEffect hook.
     */
    const getOrgProfile = async () => {
      try {
        const response = await getData(
          `/api/profile/organization/${referenceId}`,
          null
        );
        if (response) setOrganization(response);
        else setIsPageLoading(false);
      } catch (error) {
        setIsPageLoading(false);
      }
    };
    getOrgProfile();
  }, [referenceId]);

  useEffect(() => {
    /**
     * Fetches the published and funded needs for a specific organization from the backend API
     * Executes when there is a change in the values of the dependencies in the useEffect hook.
     */
    const getValidatedNeeds = async () => {
      try {
        const response = await postData(`/api/profile/getValidatedNeeds`, {
          referenceId,
          type: "Organization",
          page: page,
          searchTerm: "",
          isPrivateProfile: false,
        });

        const updatedNeeds = transformProfilePicture(response.needs);
        setNeeds((prevNeeds) => [...prevNeeds, ...updatedNeeds]);
        if (response.count != null) setTotalRecords(response.count);

        if (page.PageNumber === 1) setIsPageLoading(false);
        else setIsLoading(false);
      } catch (error) {
        if (page.PageNumber === 1) setIsPageLoading(false);
        else setIsLoading(false);
      }
    };

    if (referenceId && Object.keys(organization).length > 0)
      getValidatedNeeds();
  }, [page, referenceId, organization]);

  /**
   * useEffect hook to handle lazy loading of more data when scrolling.
   * Attaches a scroll event listener to the provided element and fetches more data
   * when the user scrolls to the bottom of the element.
   * Removes the event listener when the component unmounts.
   * Executes when there is a change in the values of the dependencies in the useEffect hook.
   */
  useEffect(() => {
    // Event handler for scrolling
    const handleScroll = () => {
      // Check if the user has scrolled to the bottom of the page
      const isAtBottom =
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight;

      if (isAtBottom && !isLoading) {
        const totalPages = Math.ceil(totalRecords / page.PageSize);
        if (page.PageNumber < totalPages) {
          // Fetch more data here
          setIsLoading(true);
          setPage((prevPage) => ({
            ...prevPage,
            PageNumber: prevPage.PageNumber + 1,
          }));
        }
      }
    };
    // Add event listener for scroll events
    window.addEventListener("scroll", handleScroll);

    return () => {
      // Remove event listener on cleanup to avoid memory leaks
      window.removeEventListener("scroll", handleScroll);
    };
  }, [page, totalRecords, isLoading]);

  return (
    <Container className="validator-list-view">
      {needs.length > 0 && (
        <Row className="justify-content-center">
          {location.key !== "default" && (
            <Col xs={12} className="text-end">
              <Link
                to={-1}
                className="text-decoration-none fw-semibold"
                title="Back"
              >
                <i className="flaticon-back pe-1 fs-5"></i>
                Back to Prior Page
              </Link>
            </Col>
          )}
          <Col xs={12} className="text-center pt-4 py-5 page-title">
            <span className="text-primary tag-line">NEEDS</span>
            <h1 className="title mt-1 fw-medium">{organization.name}</h1>
          </Col>
          {needs.map((need) => (
            <Col
              xs={8}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              xxl={3}
              key={need.referenceId}
              className="grid-list"
            >
              <NeedCard need={need} />
            </Col>
          ))}
          {isLoading && (
            <Col xs={12} className="text-center my-5" aria-labelledby="Loading">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Col>
          )}
        </Row>
      )}

      {!isPageLoading && Object.keys(needs).length === 0 && (
        <Row className="justify-content-center text-center">
          <Col xs={10} sm={12} md={9} lg={7} xl={6} xxl={5}>
            <Card className="border-0 shadow p-4 rounded-4 text-center">
              <Card.Body>
                <Image src={noData} alt="No Data Found" fluid />
                <h4 className="title mt-3">No Data Found</h4>
                <p className="mb-0 fw-light text-dark-emphasis">
                  There are no needs to show here right now.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default NeedsByOrganization;
